import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FocusHeader } from '../components/FocusHeader';
import { StepIndicator } from '../components/StepIndicator';
import { StepProgressIndicator, ProgressStep } from '../components/StepProgressIndicator';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase';
import { getWizardSteps } from '../lib/wizardSteps';
import { WIZARD_EXIT_ROUTE, WIZARD_LEAVE_HINT, WIZARD_PATHS, sessionIdFromLocation, wizardUrl } from '../lib/wizardRoutes';
import { waitForSessionUpdate } from '../lib/navigationHelpers';
import { logGenerationError } from '../lib/errorLogger';

const TITLE_GENERATION_TIMEOUT = 45000;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 2000;

interface FetchWithTimeoutOptions {
  timeout: number;
  signal?: AbortSignal;
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit & FetchWithTimeoutOptions
): Promise<Response> {
  const { timeout, signal, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Timeout: Het duurt te lang, probeer opnieuw');
    }
    throw error;
  }
}

async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function TitleGenerate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, user, currentIdea } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardSession, setDashboardSession] = useState<any>(null);
  const [dashboardUser, setDashboardUser] = useState<any>(null);
  const [dashboardIdea, setDashboardIdea] = useState<any>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const isMountedRef = useRef(true);
  const hasStartedRef = useRef(false);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([
    { label: 'Artikel ophalen', status: 'pending' },
    { label: 'Prompt voorbereiden', status: 'pending' },
    { label: 'AI titels genereren', status: 'pending' },
    { label: 'Titels opslaan', status: 'pending' },
  ]);

  const updateStepStatus = (stepIndex: number, status: 'pending' | 'in_progress' | 'completed' | 'error') => {
    setProgressSteps(prev => prev.map((step, idx) =>
      idx === stepIndex ? { ...step, status } : step
    ));
  };

  const sessionId = sessionIdFromLocation(location);

  useEffect(() => {
    if (sessionId) {
      loadDashboardSession(sessionId);
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const loadDashboardSession = async (sessionId: string) => {
    try {
      setLoading(true);

      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();

      if (sessionError || !sessionData) {
        console.error('Error loading session:', sessionError);
        setLoading(false);
        return;
      }

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', sessionData.user_id)
        .maybeSingle();

      if (userError || !userData) {
        console.error('Error loading user:', userError);
        setLoading(false);
        return;
      }

      const { data: ideaData, error: ideaError } = await supabase
        .from('blog_ideas')
        .select('*')
        .eq('id', sessionData.approved_idea_id)
        .maybeSingle();

      if (ideaError || !ideaData) {
        console.error('Error loading idea:', ideaError);
      }

      setDashboardSession(sessionData);
      setDashboardUser(userData);
      setDashboardIdea(ideaData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading dashboard session:', err);
      setLoading(false);
    }
  };

  const activeSession = dashboardSession || session;
  const activeUser = dashboardUser || user;
  const activeIdea = dashboardIdea || currentIdea;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (error) return;
    if (!activeSession || !activeUser) return;
    if (isGenerating) return;
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const generateTitles = async () => {
      setIsGenerating(true);
      try {
        console.log('Checking for existing titles...');
        updateStepStatus(0, 'in_progress');

        const { data: sessionData, error: sessionError } = await supabase
          .from('sessions')
          .select('titles, article_content')
          .eq('id', activeSession.id)
          .maybeSingle();

        if (!sessionData?.article_content) {
          console.error('No article content found in session');
          updateStepStatus(0, 'error');
          throw new Error('Er is geen artikel beschikbaar om titels voor te genereren');
        }

        if (sessionError) {
          console.error('Error fetching session:', sessionError);
        }

        if (sessionData?.titles && Array.isArray(sessionData.titles) && sessionData.titles.length > 0) {
          console.log('Using existing titles:', sessionData.titles);
          if (!isMountedRef.current) return;
          navigate(wizardUrl(WIZARD_PATHS.selectTitle, activeSession.id), { replace: true });
          return;
        }

        const inflightKey = `gen_inflight_titles_${activeSession.id}`;
        const inflightSince = Number(sessionStorage.getItem(inflightKey) || 0);
        if (inflightSince && Date.now() - inflightSince < 3 * 60 * 1000) {
          // Een eerdere aanroep loopt waarschijnlijk nog (terug, Sluiten of refresh tijdens het schrijven):
          // wacht op het resultaat in plaats van opnieuw te betalen.
          console.log('[TitleGenerate] Generation already in flight, waiting for result...');
          updateStepStatus(0, 'completed');
          updateStepStatus(1, 'completed');
          updateStepStatus(2, 'in_progress');
          const arrived = await waitForSessionUpdate(activeSession.id, 'titles', 45, 2000);
          if (arrived) {
            sessionStorage.removeItem(inflightKey);
            if (!isMountedRef.current) return;
            navigate(wizardUrl(WIZARD_PATHS.selectTitle, activeSession.id), { replace: true });
            return;
          }
          sessionStorage.removeItem(inflightKey);
        }
        sessionStorage.setItem(inflightKey, String(Date.now()));

        updateStepStatus(0, 'completed');
        updateStepStatus(1, 'in_progress');

        console.log('Generating new titles...');
        console.log('Article content length:', sessionData.article_content.length);

        const { data: { session } } = await supabase.auth.getSession();
        const authToken = session?.access_token || (import.meta.env.VITE_SUPABASE_ANON_KEY);

        let lastError: Error | null = null;
        let titlesData: any = null;
        let creditError = false;

        for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
          try {
            setRetryAttempt(attempt);
            console.log(`[TitleGenerate] Attempt ${attempt}/${MAX_RETRY_ATTEMPTS}`);

            const titlesResponse = await fetchWithTimeout(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-generate`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${authToken}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  type: 'titles',
                  session_id: activeSession.id,
                  user_id: activeUser.id,
                  data: {
                    idea_title: activeIdea?.idea_title || '',
                    idea_content: activeIdea?.idea_content || '',
                    article_content: sessionData.article_content,
                  },
                }),
                timeout: TITLE_GENERATION_TIMEOUT,
              }
            );

            titlesData = await titlesResponse.json();

            if (!titlesResponse.ok) {
              console.error(`[TitleGenerate] API error on attempt ${attempt}:`, titlesData);
              const isInsufficientCredits = titlesResponse.status === 402 || titlesData.error === 'INSUFFICIENT_CREDITS';
              if (isInsufficientCredits) {
                throw Object.assign(new Error('Onvoldoende credits om titels te genereren. Koop extra credits om door te gaan.'), { isCreditError: true });
              }
              throw new Error(titlesData.error || 'Failed to generate titles');
            }

            console.log(`[TitleGenerate] Success on attempt ${attempt}`);
            lastError = null;
            break;
          } catch (error) {
            lastError = error as Error;
            console.error(`[TitleGenerate] Error on attempt ${attempt}:`, error);

            if ((error as any).isCreditError) {
              creditError = true;
              break;
            }

            const errorMessage = error instanceof Error ? error.message : String(error);
            const isTimeout = errorMessage.includes('Timeout');
            const isNetwork = errorMessage.includes('fetch') || errorMessage.includes('network');

            await logGenerationError({
              generationType: 'blog_title',
              errorCategory: isTimeout ? 'TIMEOUT' : isNetwork ? 'NETWORK_ERROR' : 'API_ERROR',
              errorMessage: errorMessage,
              technicalError: error instanceof Error ? error.stack : undefined,
              retryAttempt: attempt - 1,
              blogId: activeSession.id,
              sessionId: activeSession.id,
              additionalContext: {
                service: 'openai',
                apiType: 'title_generation',
                timeout: TITLE_GENERATION_TIMEOUT,
                lastError: errorMessage,
                hasArticleContent: !!sessionData?.article_content,
                articleContentLength: sessionData?.article_content?.length,
                ideaTitle: activeIdea?.idea_title,
                maxRetries: MAX_RETRY_ATTEMPTS,
                attemptNumber: attempt,
              },
            });

            if (attempt < MAX_RETRY_ATTEMPTS) {
              console.log(`[TitleGenerate] Waiting ${RETRY_DELAY}ms before retry...`);
              await sleep(RETRY_DELAY);
            }
          }
        }

        if (lastError) {
          updateStepStatus(2, 'error');

          await logGenerationError({
            generationType: 'blog_title',
            errorCategory: 'TIMEOUT',
            errorMessage: `Na ${MAX_RETRY_ATTEMPTS} pogingen niet gelukt`,
            technicalError: lastError.stack,
            retryAttempt: MAX_RETRY_ATTEMPTS,
            blogId: activeSession.id,
            sessionId: activeSession.id,
            additionalContext: {
              service: 'openai',
              apiType: 'title_generation',
              timeout: TITLE_GENERATION_TIMEOUT,
              lastError: lastError.message,
              maxRetries: MAX_RETRY_ATTEMPTS,
              hasArticleContent: !!sessionData?.article_content,
            },
          });

          if (creditError) throw lastError;

          throw new Error(
            `Na ${MAX_RETRY_ATTEMPTS} pogingen niet gelukt. Probeer het later opnieuw of neem contact op met support.`
          );
        }

        if (!titlesData) {
          updateStepStatus(2, 'error');
          throw new Error('Geen resultaat ontvangen van AI');
        }

        setRetryAttempt(0);
        updateStepStatus(1, 'completed');
        updateStepStatus(2, 'in_progress');

        console.log('Titles API response:', titlesData);

        let titles = [];

        // NEW: Parse data property (current edge function format)
        if (titlesData.data) {
          try {
            const parsed = typeof titlesData.data === 'string'
              ? JSON.parse(titlesData.data)
              : titlesData.data;
            if (Array.isArray(parsed)) {
              titles = parsed;
            }
          } catch (e) {
            console.error('Failed to parse titles data:', e);
          }
        }

        // FALLBACK: Old formats (backwards compatible)
        if (titles.length === 0) {
          if (Array.isArray(titlesData.result)) {
            titles = titlesData.result;
          } else if (titlesData.result?.titles && Array.isArray(titlesData.result.titles)) {
            titles = titlesData.result.titles;
          }
        }

        if (titles.length === 0) {
          console.error('No valid titles received. Full response:', JSON.stringify(titlesData, null, 2));
          updateStepStatus(2, 'error');
          throw new Error('Geen titels ontvangen van de API');
        }

        updateStepStatus(2, 'completed');
        updateStepStatus(3, 'in_progress');

        const { error: updateError } = await supabase
          .from('sessions')
          .update({ titles: titles })
          .eq('id', activeSession.id);

        if (updateError) {
          console.error('Error saving titles:', updateError);
          updateStepStatus(3, 'error');
          throw new Error('Fout bij opslaan van titels');
        }

        sessionStorage.removeItem(inflightKey);

        console.log('Verifying titles were saved to database...');
        const verified = await waitForSessionUpdate(activeSession.id, 'titles', 15, 300);

        if (!verified) {
          console.warn('Could not verify titles in database within timeout, but continuing anyway');
          console.log('Titles that were returned:', titles);
        }

        updateStepStatus(3, 'completed');
        console.log('Titles verified in database!');

        await new Promise(resolve => setTimeout(resolve, 500));

        console.log('Navigating with titles:', titles);
        if (!isMountedRef.current) return;
        navigate(wizardUrl(WIZARD_PATHS.selectTitle, activeSession.id), { replace: true });
        setIsGenerating(false);
      } catch (err) {
        sessionStorage.removeItem(`gen_inflight_titles_${activeSession.id}`);
        console.error('Error generating titles:', err);

        await logGenerationError({
          generationType: 'blog_title',
          errorCategory: 'UNKNOWN',
          errorMessage: err instanceof Error ? err.message : 'Unknown error',
          technicalError: err instanceof Error ? err.stack : undefined,
          retryAttempt: retryAttempt,
          blogId: activeSession?.id,
          sessionId: activeSession?.id,
          additionalContext: {
            service: 'openai',
            apiType: 'title_generation',
            timeout: TITLE_GENERATION_TIMEOUT,
            lastError: err instanceof Error ? err.message : String(err),
          },
        });

        setError(
          `Er ging iets mis bij het genereren van titels: ${err instanceof Error ? err.message : 'Unknown error'}`
        );
        setIsGenerating(false);
        setRetryAttempt(0);
      }
    };

    generateTitles();
  }, [activeSession, activeUser, activeIdea, loading, isGenerating, error]);

  const steps = getWizardSteps(activeSession, 4);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Titels genereren"
          onClose={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
          hideSaveStatus
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E94E87] mx-auto"></div>
              <p className="text-gray-600">Laden...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Titels genereren"
          onClose={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
          hideSaveStatus
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="card bg-red-50 border-red-200">
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => navigate(wizardUrl(WIZARD_PATHS.articleEdit, activeSession?.id), { replace: true })}
              className="btn-secondary mt-4"
            >
              Terug naar artikel
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (!loading && !activeSession) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader title="Titels genereren" onClose={() => navigate(WIZARD_EXIT_ROUTE)} hideSaveStatus />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="card bg-red-50 border-red-200">
            <p className="text-red-800">Geen sessie gevonden. Open de blog opnieuw vanuit je concepten.</p>
            <button onClick={() => navigate(WIZARD_EXIT_ROUTE)} className="btn-secondary mt-4">Naar Mijn Blogs</button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <FocusHeader
        title="Titels genereren"
        onClose={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
        hideSaveStatus
      />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <StepIndicator steps={steps} interactive={false} />
        <div className="space-y-6 mt-6">
          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold text-[#231F20]">
              We maken titels voor je...
            </h1>
            <p className="text-gray-600">Een moment geduld.</p>
            <p className="text-sm text-gray-500">{WIZARD_LEAVE_HINT}</p>
          </div>
          <StepProgressIndicator steps={progressSteps} />
        </div>
      </main>
    </div>
  );
}
